import { Button, Icon, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@hero/components/atoms';
import { PageLayout } from '@hero/components/layouts';
import { SettingsTabs } from '@hero/components/organisms';
import { PAYMENT_METHOD_TYPES } from '@hero/constants';
import { ShowMore } from '@hero/molecules';
import { useGetPaymentMethods, useGetProviderUrl } from '@hero/services/organization-service/hooks';
import { addPaymentMethodLink } from '@hero/utils';

import * as S from './style';

export default function PaymentMethodsPage() {
  const { t } = useTranslation();

  const { data: providerUrl } = useGetProviderUrl();
  const { data: { paymentMethods } = {}, isLoading: isPaymentMethodsLoading } =
    useGetPaymentMethods();

  const handlePaymentMethodLink = () => {
    if (providerUrl) {
      window.open(addPaymentMethodLink(providerUrl?.loginLink, window.location.href), '_self');
    }
  };

  const isHouseAccount = useMemo(() => {
    return (
      !!paymentMethods &&
      paymentMethods?.find(
        paymentMethod => paymentMethod.type === PAYMENT_METHOD_TYPES.houseAccount
      )
    );
  }, [paymentMethods]);

  return (
    <PageLayout
      header={{
        title: t('settings')
      }}
    >
      <SettingsTabs />

      <Card style={{ gap: 16, maxWidth: 1280 }}>
        <S.InfoHeaderWrapper>
          <Typography variant="body1Bold">
            {t(isHouseAccount ? 'houseAccount' : 'creditCardAccount')}
          </Typography>
          {!isPaymentMethodsLoading && !isHouseAccount && (
            <>
              <VerticalDivider $height={21} />
              <Button
                variant="link"
                startIcon={<Icon name="external-link" />}
                onClick={handlePaymentMethodLink}
              >
                {t('addNewCard')}
              </Button>
            </>
          )}
        </S.InfoHeaderWrapper>

        {!isHouseAccount ? (
          paymentMethods?.length ? (
            <ShowMore container={S.PaymentMethodArea} type="array" numberOfLines={6}>
              {paymentMethods?.map(paymentMethod => {
                return (
                  <S.PaymentMethodItem key={paymentMethod?.code} isRadioButtonShown={false}>
                    <Typography
                      style={{
                        overflow: 'hidden',
                        lineClamp: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {paymentMethod?.name}
                    </Typography>
                  </S.PaymentMethodItem>
                );
              })}
            </ShowMore>
          ) : (
            <EmptyArea />
          )
        ) : (
          <Typography variant="body1" emphasis="weak" style={{ fontStyle: 'italic' }}>
            {t('yourOrganizationUsingHouseAccount')}
          </Typography>
        )}
      </Card>
    </PageLayout>
  );
}

function EmptyArea() {
  const { t } = useTranslation();
  const { data: providerUrl } = useGetProviderUrl();

  const currentPath = window.location.href;

  const handlePaymentMethodLink = () => {
    if (providerUrl) {
      window.open(addPaymentMethodLink(providerUrl?.loginLink, currentPath), '_self');
    }
  };

  return (
    <S.EmptyArea>
      <Icon name="payment-method" size="xxl" color="var(--color-accent-primary-default)" />
      <S.EmptyAreaText variant="body2SemiBold">{t('addFirstPM')}!</S.EmptyAreaText>
      <Button startIcon={<Icon name="add" />} onClick={handlePaymentMethodLink} size="md">
        {t('addPaymentMethod')}
      </Button>
    </S.EmptyArea>
  );
}
